import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { getAllPayments } from 'src/shared/services/paymentService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { Box } from '@mui/system';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { InvoicePayment } from '../models/invoicePayment';
import { PaymentsSearchFilter } from '../models/paymentsSearchFilter';
import { PaymentDetail } from './paymentDetail';
import { PaymentStatuses } from './paymentStatuses';

type PaymentsDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: PaymentsSearchFilter;
  searchFilterCallback: (filter: PaymentsSearchFilter) => void;
};

export const PaymentsDataGrid = (props: PaymentsDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [payment, setPayment] = useState<InvoicePayment>();
  const [rows, setRows] = useState<InvoicePayment[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = <K extends keyof DataGridModel>(k: K, v: DataGridModel[K]) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    loadingCallback(true);
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAllPayments(searchFilter);
      const allPaymentsResult = extendObjectWithId(result.data.results, 'invoicePayment_id');
      updateData('totalRows', result.data.totalItems);

      setTimeout(() => {
        setRows(allPaymentsResult);
      }, 100);
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onRowClick = (params) => {
    if (params.row) {
      setPayment(params.row);
      setOpenModal(true);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'invoicePayment_id',
      headerName: t('invoice:payments.tableHeader.paymentId'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.invoicePayment_id}</>;
      },
    },
    {
      field: 'date',
      headerName: t('invoice:payments.tableHeader.date'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDate(params.row.date)}</>;
      },
    },
    {
      field: 'amount',
      headerName: t('invoice:payments.tableHeader.amount'),
      sortable: true,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return <AmountWithCurrency amount={params.row.amount} />;
      },
    },
    {
      field: 'ocr',
      headerName: t('invoice:payments.tableHeader.invoice'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.ocr}</>;
      },
    },
    {
      field: 'verificationNo',
      headerName: t('invoice:payments.tableHeader.verificationNo'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.verificationNo}</>;
      },
    },
    {
      field: 'source',
      headerAlign: 'left',
      align: 'left',
      headerName: t('invoice:payments.tableHeader.source'),
      sortable: true,
      type: 'number',
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.source}</>;
      },
    },
    {
      field: 'status',
      headerName: t('invoice:payments.tableHeader.status'),
      sortable: false,
      width: 100,
      renderCell: (params: GridCellParams) => {
        return <PaymentStatuses status={params.row.status} chipSize='small' />;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          '& .MuiPaper-root': {
            boxShadow: 'none',
          },
        }}
        ref={scrollRef}
      >
        <DataGrid<InvoicePayment>
          autoHeight
          columns={columns}
          rows={rows}
          disableSelectionOnClick
          onRowClick={onRowClick}
          dataModel={data}
          checkboxSelection={false}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          selectionModel={[]}
          sortModel={[
            {
              field: searchFilter.sortCol,
              sort: searchFilter.sortDir,
            },
          ]}
          onSortModelChange={(newSortModel) => {
            if (newSortModel.length > 0) {
              const { field } = newSortModel[0];
              searchFilterCallback({
                ...searchFilter,
                pageIndex: 0,
                sortCol: field,
                sortDir: newSortModel[0].sort,
              });
            } else {
              searchFilterCallback({
                ...searchFilter,
                pageIndex: 0,
                sortCol: searchFilter.sortCol,
                sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
              });
            }
          }}
          onSelectionModelChange={() => ConsoleHelper.log('')}
        />
      </Box>
      {payment && openModal && <PaymentDetail key={payment.invoicePayment_id} setOpenModal={setOpenModal} payment={payment} />}
    </>
  );
};
